import { Button, Dialog, Portal } from '@chakra-ui/react'

/**
 * @component DeleteActivityModal
 * @description
 * A confirmation modal dialog used to delete an existing activity.
 *
 * It presents the user with:
 * - A **warning message** stating that the deletion is irreversible.
 * - Two action buttons: **Cancel** (outline) and **Delete** (destructive red).
 *
 * Includes:
 * - Simple controlled open/close state via props.
 * - Lazy mounting for performance optimization.
 * - Portal rendering to avoid z-index issues.
 * - Accessible dialog structure with proper header, body, and footer.
 *
 * @prop {boolean} open - Controls whether the modal is visible.
 * @prop {() => void} onDelete - Callback fired when the user confirms deletion.
 * @prop {() => void} onClose - Callback fired when the user cancels or closes the modal.
 *
 * @example
 * ```tsx
 * <DeleteActivityModal
 *   open={isDeleteModalOpen}
 *   onDelete={() => {
 *     deleteActivity(activityId);
 *     setIsDeleteModalOpen(false);
 *   }}
 *   onClose={() => setIsDeleteModalOpen(false)}
 * />
 * ```
 */
interface DeleteActivityModalProps {
  open: boolean
  onDelete: () => void
  onClose: () => void
  loading: boolean
}

export default function DeleteActivityModal({
  open,
  onDelete,
  onClose,
  loading,
}: DeleteActivityModalProps) {
  return (
    <Dialog.Root
      lazyMount
      open={open}
      onOpenChange={({ open: nextOpen }) => {
        if (!nextOpen) onClose()
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Aktivität löschen</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              Möchten Sie diese Aktivität löschen? Dieser Vorgang kann nicht rückgängig gemacht
              werden.
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button colorPalette="gray" variant="outline">
                  Abbrechen
                </Button>
              </Dialog.ActionTrigger>
              <Button variant="solid" disabled={loading} colorPalette="red" onClick={onDelete}>
                Aktivität löschen
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
